/**
 * Deterministic Story Flow Tutorial - Intermediate
 *
 * A 3-agent pipeline that generates a story outline, checks quality using
 * structured output, applies conditional logic, and writes the final story.
 * Based on the OpenAI Agents SDK deterministic.py example.
 */
import { TutorialStep } from '../../types/tutorial';

/**
 * Specific values for the deterministic story flow tutorial.
 * Based on the OpenAI Agents SDK deterministic.py example.
 */
export const DETERMINISTIC_TUTORIAL_VALUES = {
  orchestration: {
    name: 'Deterministic Story Flow',
    description:
      'A 3-agent pipeline that generates a story outline, checks quality, and writes the final story.',
  },
  agents: {
    storyOutline: {
      name: 'Story Outline Agent',
      instructions:
        "Generate a very short story outline based on the user's input. Include a beginning, middle, and end. Keep it to 3-5 bullet points.",
    },
    outlineChecker: {
      name: 'Outline Checker Agent',
      instructions:
        'Read the given story outline, and judge the quality. Determine if it is a sci-fi story. Output your assessment as structured data.',
    },
    storyWriter: {
      name: 'Story Writer Agent',
      instructions:
        'Write a short story (200-300 words) based on the given outline. Make it engaging and well-structured with vivid descriptions.',
    },
  },
  model: {
    className: 'OutlineCheckerOutput',
    description: 'Structured output for outline quality assessment',
    properties: [
      { name: 'good_quality', type: 'bool' },
      { name: 'is_scifi', type: 'bool' },
    ],
  },
  userPrompt: {
    label: 'Get Story Idea',
    inputMessage:
      "Enter a story idea and I'll create a complete story through a multi-agent pipeline!",
  },
};

const ORCH_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Deterministic Story Flow" workflow.';

/**
 * The 20 tutorial steps for the deterministic story flow tutorial.
 *
 * This tutorial assumes the user already has a project (from core_workflow or their own).
 * It creates agents inline from the workflow editor and a model from the agent form.
 *
 * Step Flow:
 * 1-3:   Navigate to Workflows, create workflow
 * 4-6:   Add User Prompt step
 * 7-9:   Add Story Outline Agent (click +, pick Agent - Create, create inline)
 * 10-12: Add Outline Checker Agent with model (pick Agent - Validate, create inline)
 * 13-15: Add Conditional step
 * 16-18: Add Story Writer Agent (pick Agent - Create, create inline)
 * 19-20: Run and view output
 */
export const DETERMINISTIC_TUTORIAL_STEPS: TutorialStep[] = [
  // --- Navigate to Workflows and create ---
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Welcome to Deterministic Flows!',
    description:
      "In this tutorial, you'll build a __3-agent pipeline__ based on the OpenAI Agents SDK deterministic flow pattern.\n\n" +
      'The pipeline works like an assembly line:\n' +
      '1. An agent generates a story outline\n' +
      '2. A checker agent evaluates quality using __structured output__\n' +
      '3. A conditional decides whether to continue\n' +
      '4. A writer agent creates the final story\n\n' +
      "You'll create all agents __directly from the workflow editor__!",
    actionPrompt: "Click 'Workflows' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Workflow',
    description:
      "Let's create a new workflow for our deterministic story flow.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Flow',
    description:
      'Set up the workflow details:\n\n' +
      '1. For Name, enter: **Deterministic Story Flow**\n\n' +
      '2. For Description, enter: **A 3-agent pipeline that generates a story outline, checks quality, and writes the final story.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 2,
  },

  // --- Add User Prompt step ---
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add User Prompt',
    description:
      'Welcome to the workflow editor! First, we need a way for users to provide their story idea.\n\n' +
      'Click the __+__ button below the __Start__ node to add a step.',
    actionPrompt: 'Click the __+__ button below the Start node.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      "Select the __User Prompt__ step type. This will collect the user's story idea.",
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 4,
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      'Configure the User Prompt:\n\n' +
      '__Label__: **Get Story Idea**\n\n' +
      "__Input Message__: **Enter a story idea and I'll create a complete story through a multi-agent pipeline!**",
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 4,
  },

  // --- Add Story Outline Agent step (with inline agent creation) ---
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Outline Agent',
    description:
      "Now we'll add the first agent in our pipeline - the __Story Outline Agent__.\n\n" +
      "This agent generates a brief story outline from the user's idea.",
    actionPrompt: 'Click the __+__ button below the User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The __Story Outline Agent__ will __generate__ a new story outline from the user's idea, so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create Story Outline Agent',
    description:
      'The __Run Agent__ step type with __Create__ intent is now selected - perfect for generating new content.\n\n' +
      'Now create the agent __directly from this page__:\n\n' +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Create from Scratch__\n' +
      '3. In the agent form, enter:\n' +
      '   - Name: **Story Outline Agent**\n' +
      "   - Instructions: **Generate a very short story outline based on the user's input. Include a beginning, middle, and end. Keep it to 3-5 bullet points.**\n" +
      '4. Click __Create Agent__ to save\n' +
      '5. The new agent will be auto-selected in the dropdown\n' +
      '6. Click __Add Step__ to add this step to the workflow',
    actionPrompt: "Create the agent inline, then click 'Add Step' when ready.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 7,
  },

  // --- Add Outline Checker Agent step (with model creation) ---
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Checker Agent',
    description:
      "Next up is the __Outline Checker Agent__. This agent evaluates the outline quality and determines if it's a sci-fi story.\n\n" +
      'This agent uses __structured output__ - a data model that defines exactly what fields the agent returns.',
    actionPrompt: 'Click the __+__ button below the outline agent step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="step-option-run_agent-validate"]',
    title: 'Select Agent - Validate',
    description:
      'Select __Agent - Validate__ for the checker agent. The validate intent is used for agents that check or assess content.',
    actionPrompt: 'Click on __Agent - Validate__.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-validate"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 10,
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create Checker Agent with Model',
    description:
      "This is the most important step! You'll create an agent with a __structured output model__.\n\n" +
      '__Create the Agent:__\n' +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Create from Scratch__\n' +
      '3. Name: **Outline Checker Agent**\n' +
      '4. Instructions: **Read the given story outline, and judge the quality. Determine if it is a sci-fi story. Output your assessment as structured data.**\n\n' +
      '__Create the Output Schema:__\n' +
      '5. Scroll to __Response Formatting__ section\n' +
      '6. Change Output Type to __Structured Data (Schema)__\n' +
      '7. Click __Create New Schema__\n' +
      '8. Set Schema Name to **OutlineCheckerOutput**\n' +
      '9. Add property: name = **good_quality**, type = **bool**\n' +
      '10. Add property: name = **is_scifi**, type = **bool**\n' +
      '11. Click __Create Schema__, then __Create Agent__\n\n' +
      'Finally, click __Add Step__ to add this step.',
    actionPrompt:
      "Create the agent with structured output schema, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    tooltipMaxWidth: 420,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 10,
  },

  // --- Add Conditional step ---
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add Conditional Step',
    description:
      "Now let's add the __decision point__! A conditional step evaluates the checker's output to decide if we should continue.\n\n" +
      "If the outline is good quality __AND__ is a sci-fi story, we'll write the full story. Otherwise, the pipeline stops.",
    actionPrompt: 'Click the __+__ button below the checker agent step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 14,
    targetSelector: '[data-tutorial^="step-option-conditional"]',
    title: 'Select Conditional',
    description:
      'Select the __Conditional__ step type. This creates a branching point in your workflow based on conditions.',
    actionPrompt: 'Click on __Conditional__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial^="step-option-conditional"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 13,
  },
  {
    stepNumber: 15,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure Conditions',
    description:
      "Set the conditions to validate that the agent's output is both good quality and science fiction:\n\n" +
      '1. For the first condition, set it to:\n' +
      '   - Field: __OutlineCheckerOutput.good_quality__\n' +
      '   - Operator: __is__\n' +
      '   - Value: __True__\n' +
      '2. Click __Add Condition__, then set the second condition:\n' +
      '   - Field: __OutlineCheckerOutput.is_scifi__\n' +
      '   - Operator: __is__\n' +
      '   - Value: __True__\n' +
      '3. Make sure both conditions use __AND__ logic\n\n' +
      'This means the pipeline only continues to write the story if the outline passes both checks.\n\n' +
      '__Note:__ The conditional also passes the checker agent\'s full output through to the true branch — the Story Writer downstream still sees the outline data, not just the decision result.\n\n' +
      'Click __Add Step__ when the conditions are configured.',
    actionPrompt: "Configure the conditions, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 13,
  },

  // --- Add Story Writer Agent step ---
  {
    stepNumber: 16,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add Story Writer Agent',
    description:
      'Almost there! Now add the final agent on the __true branch__ of the conditional.\n\n' +
      'The __Story Writer Agent__ takes the outline and writes a complete short story.',
    actionPrompt:
      'Click the __+__ button on the true branch of the conditional.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 17,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      'Select __Agent - Create__ for the story writer. The create intent generates new content - perfect for writing the final story.',
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 16,
  },
  {
    stepNumber: 18,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create Story Writer Agent',
    description:
      'Create the final agent inline:\n\n' +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Create from Scratch__\n' +
      '3. Name: **Story Writer Agent**\n' +
      '4. Instructions: **Write a short story (200-300 words) based on the given outline. Make it engaging and well-structured with vivid descriptions.**\n' +
      '5. Click __Create Agent__\n' +
      '6. Click __Add Step__',
    actionPrompt: "Create the agent inline, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
    resumeFallbackStep: 16,
  },

  // --- Run and view output ---
  {
    stepNumber: 19,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Run Your Pipeline!',
    description:
      'Your deterministic flow is complete! The pipeline will:\n\n' +
      '• Ask for a story idea\n' +
      '• Generate an outline\n' +
      "• Check if it's good quality and sci-fi\n" +
      '• Write the full story (if checks pass)\n\n' +
      'Try entering: **A robot discovers emotions on a space station**',
    actionPrompt: "Click 'Execute' to run your pipeline!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
  {
    stepNumber: 20,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View Your Story!',
    description:
      'Your pipeline is running! Once complete, check out __View Final Output__ to see the story.\n\n' +
      "__What you've learned:__\n" +
      '• Creating agents __inline__ from the workflow editor\n' +
      '• Building __structured output models__ for agent responses\n' +
      '• Using __conditional steps__ for decision logic\n' +
      '• Building a __multi-agent pipeline__ where agents work in sequence\n\n' +
      '__Tip:__ Three agents and a structured-output model is a lot of setup to lose. From the Workflows ' +
      'page, click the __Duplicate__ icon to copy this pipeline into another project - the three agents ' +
      'and the OutlineCheckerOutput model are auto-cloned so the copy works without re-wiring.\n\n' +
      "Click __Finish Tutorial__ when you're ready!",
    actionPrompt: "View the output, then click 'Finish Tutorial'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: ORCH_ROUTE_MSG,
  },
];
